/**
 * ConversionPullEndpointPage — Trackier-style CRUD list for the
 * `/api/v1/conversion-pull-endpoint` BE (FS-238).
 *
 * Layout (top → bottom):
 *   1. Topbar: title + subtitle + "Create conversion" button.
 *   2. Filter bar: text search (offer_id), date range (since/until), status chips.
 *   3. Table: event, amount (payout+currency), status badge, created_at.
 *      States: loading skeleton, empty, error banner, row-list.
 *   4. Create drawer: zod + react-hook-form, all seed fields + status enum.
 *
 * Brand (per CLAUDE.md):
 *   - green-600 / green-700 / green-50 accent only (no emerald→sky gradient).
 *   - white surface + zinc-200 borders, Inter via system sans stack.
 *   - 6-8px rounding. Inline SVG icons (no lucide, no emoji).
 *   - No dark mode, no competitor names.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	CONVERSION_STATUSES,
	STATUS_LABELS,
	createConversionSchema,
	formatAmount,
	formatTimestamp,
	statusBadgeClass,
	type ConversionRow,
	type ConversionStatus,
	type CreateConversionBody,
	type CreateConversionForm,
	type ListConversionsParams,
} from "./api";
import {
	useConversionPullEndpointCreate,
	useConversionPullEndpointList,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50";
const BTN_DANGER =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const INPUT =
	"w-full rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 transition-colors focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const CARD = "rounded-[8px] border border-zinc-200 bg-white shadow-sm";
const LABEL = "text-xs font-medium text-zinc-600";

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function IconPlus() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}
function IconSearch() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
			<circle cx="11" cy="11" r="7" />
			<path d="m20 20-3.5-3.5" />
		</svg>
	);
}
function IconCalendar() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
			<rect x="3" y="5" width="18" height="16" rx="2" />
			<path d="M8 3v4M16 3v4M3 10h18" />
		</svg>
	);
}
function IconX() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
			<path d="M6 6l12 12M18 6L6 18" />
		</svg>
	);
}
function IconRefresh() {
	return (
		<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
			<path d="M3 12a9 9 0 0 1 15-6.7l3 2.7" />
			<path d="M21 3v6h-6" />
			<path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
			<path d="M3 21v-6h6" />
		</svg>
	);
}
function IconInbox() {
	return (
		<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			<path d="M22 12h-6l-2 3h-4l-2-3H2" />
			<path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Filter helpers
// ---------------------------------------------------------------------------

function dateInputToUnix(v: string): number | null {
	if (!v) return null;
	const ms = Date.parse(v);
	if (!Number.isFinite(ms)) return null;
	return Math.floor(ms / 1000);
}

interface FilterState {
	offerId: string;
	sinceDate: string;
	untilDate: string;
	statuses: ConversionStatus[];
}

const EMPTY_FILTERS: FilterState = {
	offerId: "",
	sinceDate: "",
	untilDate: "",
	statuses: [],
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ConversionPullEndpointPage() {
	const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const listParams = useMemo<ListConversionsParams>(() => {
		const p: ListConversionsParams = { per_page: 100 };
		const offer = filters.offerId.trim();
		if (offer) p.offer_id = offer;
		const since = dateInputToUnix(filters.sinceDate);
		if (since != null) p.since = since;
		const untilBase = dateInputToUnix(filters.untilDate);
		if (untilBase != null) {
			// Treat "until" as inclusive end-of-day.
			p.until = untilBase + 86_399;
		}
		if (filters.statuses.length > 0) p.status = filters.statuses;
		return p;
	}, [filters]);

	const listQuery = useConversionPullEndpointList(listParams);
	const createMut = useConversionPullEndpointCreate();

	const rows = listQuery.data?.data ?? [];
	const meta = listQuery.data?.meta;

	const toggleStatus = (s: ConversionStatus) => {
		setFilters((prev) => ({
			...prev,
			statuses: prev.statuses.includes(s)
				? prev.statuses.filter((x) => x !== s)
				: [...prev.statuses, s],
		}));
	};

	const hasFilters =
		filters.offerId !== "" ||
		filters.sinceDate !== "" ||
		filters.untilDate !== "" ||
		filters.statuses.length > 0;

	return (
		<main
			data-testid="conversion-pull-endpoint-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10 font-sans text-zinc-900"
		>
			{/* Topbar */}
			<header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex flex-col gap-1">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						Reporting
					</p>
					<h1
						className="text-2xl font-semibold text-zinc-900"
						data-testid="conversion-pull-endpoint-title"
					>
						Conversion Pull Endpoint
					</h1>
					<p className="text-sm text-zinc-500">
						Serve conversions to partners via a pull API. View, filter, and
						seed events.
					</p>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className={BTN_GHOST}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
						data-testid="conversion-pull-endpoint-refresh"
						aria-label="Refresh"
					>
						<IconRefresh />
						<span>{listQuery.isFetching ? "Refreshing" : "Refresh"}</span>
					</button>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={() => setDrawerOpen(true)}
						data-testid="conversion-pull-endpoint-open-create"
					>
						<IconPlus />
						<span>Create conversion</span>
					</button>
				</div>
			</header>

			{/* Filter bar */}
			<section
				className={`${CARD} p-4`}
				aria-label="Filters"
				data-testid="conversion-pull-endpoint-filters"
			>
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Offer ID</span>
						<span className="relative">
							<span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400">
								<IconSearch />
							</span>
							<input
								type="text"
								className={`${INPUT} pl-8`}
								placeholder="Search by offer"
								value={filters.offerId}
								onChange={(e) =>
									setFilters((p) => ({ ...p, offerId: e.currentTarget.value }))
								}
								data-testid="conversion-pull-endpoint-offer-input"
							/>
						</span>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>From</span>
						<span className="relative">
							<span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400">
								<IconCalendar />
							</span>
							<input
								type="date"
								className={`${INPUT} pl-8`}
								value={filters.sinceDate}
								onChange={(e) =>
									setFilters((p) => ({
										...p,
										sinceDate: e.currentTarget.value,
									}))
								}
								data-testid="conversion-pull-endpoint-since-input"
							/>
						</span>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>To</span>
						<span className="relative">
							<span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-zinc-400">
								<IconCalendar />
							</span>
							<input
								type="date"
								className={`${INPUT} pl-8`}
								value={filters.untilDate}
								onChange={(e) =>
									setFilters((p) => ({
										...p,
										untilDate: e.currentTarget.value,
									}))
								}
								data-testid="conversion-pull-endpoint-until-input"
							/>
						</span>
					</label>
				</div>
				<div className="mt-3 flex flex-wrap items-center gap-2">
					<span className={LABEL}>Status</span>
					{CONVERSION_STATUSES.map((s) => {
						const active = filters.statuses.includes(s);
						return (
							<button
								key={s}
								type="button"
								className={
									"rounded-full border px-3 py-1 text-xs font-medium transition-colors " +
									(active
										? "border-green-600 bg-green-50 text-green-700"
										: "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50")
								}
								onClick={() => toggleStatus(s)}
								data-testid={`conversion-pull-endpoint-status-chip-${s}`}
								aria-pressed={active}
							>
								{STATUS_LABELS[s]}
							</button>
						);
					})}
					{hasFilters ? (
						<button
							type="button"
							className="ml-auto inline-flex items-center gap-1 rounded-[6px] px-2 py-1 text-xs font-medium text-zinc-500 hover:text-zinc-700"
							onClick={() => setFilters(EMPTY_FILTERS)}
							data-testid="conversion-pull-endpoint-clear-filters"
						>
							<IconX />
							<span>Clear</span>
						</button>
					) : null}
				</div>
			</section>

			{/* Table */}
			<section
				className={CARD}
				aria-label="Conversions list"
				data-testid="conversion-pull-endpoint-list-card"
			>
				<div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
					<h2 className="text-sm font-semibold text-zinc-900">Conversions</h2>
					<span
						className="text-xs text-zinc-500"
						data-testid="conversion-pull-endpoint-total"
					>
						{meta
							? `${rows.length.toLocaleString()} of ${meta.total.toLocaleString()}`
							: listQuery.isLoading
								? "Loading"
								: "—"}
					</span>
				</div>

				{listQuery.error ? (
					<ErrorBanner message={(listQuery.error as Error).message} />
				) : listQuery.isLoading ? (
					<LoadingSkeleton />
				) : rows.length === 0 ? (
					<EmptyState onCreate={() => setDrawerOpen(true)} />
				) : (
					<ConversionsTable rows={rows} />
				)}
			</section>

			{/* Create drawer */}
			{drawerOpen ? (
				<CreateDrawer
					onClose={() => setDrawerOpen(false)}
					onSubmit={async (values) => {
						await createMut.mutateAsync(values);
						setDrawerOpen(false);
					}}
					submitting={createMut.isPending}
					errorMessage={createMut.error?.message ?? null}
				/>
			) : null}
		</main>
	);
}

export default ConversionPullEndpointPage;

// ---------------------------------------------------------------------------
// Table + states
// ---------------------------------------------------------------------------

function ConversionsTable({ rows }: { rows: ConversionRow[] }) {
	return (
		<div className="overflow-x-auto" data-testid="conversion-pull-endpoint-list">
			<table className="w-full border-collapse text-sm">
				<thead>
					<tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
						<th className="px-5 py-2.5">Event</th>
						<th className="px-5 py-2.5">Offer</th>
						<th className="px-5 py-2.5">Amount</th>
						<th className="px-5 py-2.5">Status</th>
						<th className="px-5 py-2.5">Created</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((r) => (
						<tr
							key={r.id}
							className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50"
							data-testid={`conversion-pull-endpoint-row-${r.id}`}
						>
							<td className="px-5 py-3">
								<div className="font-medium text-zinc-900">{r.event}</div>
								<div className="font-mono text-xs text-zinc-400">
									{r.click_id}
								</div>
							</td>
							<td className="px-5 py-3 font-mono text-xs text-zinc-700">
								{r.offer_id}
							</td>
							<td className="px-5 py-3 tabular-nums text-zinc-900">
								{formatAmount(r.payout, r.currency)}
							</td>
							<td className="px-5 py-3">
								<span
									className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(r.status)}`}
								>
									{STATUS_LABELS[r.status]}
								</span>
							</td>
							<td className="px-5 py-3 text-xs text-zinc-500 tabular-nums">
								{formatTimestamp(r.created_at)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function LoadingSkeleton() {
	return (
		<div
			className="divide-y divide-zinc-100"
			data-testid="conversion-pull-endpoint-loading"
			role="status"
			aria-live="polite"
		>
			{Array.from({ length: 4 }).map((_, i) => (
				<div key={i} className="flex items-center gap-4 px-5 py-4">
					<div className="h-3 w-24 animate-pulse rounded bg-zinc-100" />
					<div className="h-3 w-32 animate-pulse rounded bg-zinc-100" />
					<div className="h-3 w-20 animate-pulse rounded bg-zinc-100" />
					<div className="h-3 w-16 animate-pulse rounded bg-zinc-100" />
					<div className="ml-auto h-3 w-28 animate-pulse rounded bg-zinc-100" />
				</div>
			))}
			<span className="sr-only">Loading conversions</span>
		</div>
	);
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
	return (
		<div
			className="flex flex-col items-center justify-center gap-3 px-5 py-16 text-center"
			data-testid="conversion-pull-endpoint-empty"
		>
			<div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-50 text-green-600">
				<IconInbox />
			</div>
			<div>
				<p className="text-sm font-semibold text-zinc-900">No conversions yet</p>
				<p className="mt-1 text-xs text-zinc-500">
					Create a test conversion to verify the pull endpoint.
				</p>
			</div>
			<button
				type="button"
				className={BTN_PRIMARY}
				onClick={onCreate}
				data-testid="conversion-pull-endpoint-empty-create"
			>
				<IconPlus />
				<span>Create conversion</span>
			</button>
		</div>
	);
}

function ErrorBanner({ message }: { message: string }) {
	return (
		<div
			role="alert"
			className="m-5 rounded-[6px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
			data-testid="conversion-pull-endpoint-error"
		>
			Failed to load conversions: {message}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Create drawer (zod + RHF)
// ---------------------------------------------------------------------------

interface DrawerProps {
	onClose: () => void;
	onSubmit: (values: CreateConversionBody) => Promise<void>;
	submitting: boolean;
	errorMessage: string | null;
}

function CreateDrawer({ onClose, onSubmit, submitting, errorMessage }: DrawerProps) {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<CreateConversionForm, unknown, CreateConversionBody>({
		resolver: zodResolver(createConversionSchema),
		defaultValues: {
			click_id: "",
			offer_id: "",
			event: "conversion",
			payout: 0,
			currency: "USD",
			status: "pending",
			tx_id: "",
		},
	});

	const submit = handleSubmit(async (values) => {
		await onSubmit(values);
	});

	return (
		<div
			className="fixed inset-0 z-40 flex items-stretch justify-end bg-zinc-900/40"
			role="dialog"
			aria-modal="true"
			aria-labelledby="conversion-pull-endpoint-drawer-title"
			data-testid="conversion-pull-endpoint-drawer"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<aside className="flex h-full w-full max-w-md flex-col border-l border-zinc-200 bg-white shadow-xl">
				<header className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
					<h2
						id="conversion-pull-endpoint-drawer-title"
						className="text-base font-semibold text-zinc-900"
					>
						Create conversion
					</h2>
					<button
						type="button"
						className="rounded-[6px] p-1 text-zinc-400 hover:bg-zinc-50 hover:text-zinc-700"
						onClick={onClose}
						aria-label="Close"
						data-testid="conversion-pull-endpoint-drawer-close"
					>
						<IconX />
					</button>
				</header>
				<form
					onSubmit={submit}
					className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-5"
					data-testid="conversion-pull-endpoint-create-form"
				>
					<Field label="Click ID" error={errors.click_id?.message}>
						<input
							type="text"
							className={INPUT}
							placeholder="clk_abc123"
							data-testid="conversion-pull-endpoint-click-input"
							{...register("click_id")}
						/>
					</Field>
					<Field label="Offer ID" error={errors.offer_id?.message}>
						<input
							type="text"
							className={INPUT}
							placeholder="offer_42"
							data-testid="conversion-pull-endpoint-offer-body-input"
							{...register("offer_id")}
						/>
					</Field>
					<div className="grid grid-cols-2 gap-3">
						<Field label="Event" error={errors.event?.message}>
							<input
								type="text"
								className={INPUT}
								data-testid="conversion-pull-endpoint-event-input"
								{...register("event")}
							/>
						</Field>
						<Field label="Status" error={errors.status?.message}>
							<select
								className={SELECT}
								data-testid="conversion-pull-endpoint-status-input"
								{...register("status")}
							>
								{CONVERSION_STATUSES.map((s) => (
									<option key={s} value={s}>
										{STATUS_LABELS[s]}
									</option>
								))}
							</select>
						</Field>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<Field label="Payout" error={errors.payout?.message}>
							<input
								type="number"
								step="0.01"
								min="0"
								className={INPUT}
								data-testid="conversion-pull-endpoint-payout-input"
								{...register("payout")}
							/>
						</Field>
						<Field label="Currency" error={errors.currency?.message}>
							<input
								type="text"
								className={INPUT}
								data-testid="conversion-pull-endpoint-currency-input"
								{...register("currency")}
							/>
						</Field>
					</div>
					<Field label="Transaction ID (optional)" error={errors.tx_id?.message}>
						<input
							type="text"
							className={INPUT}
							placeholder="tx_..."
							data-testid="conversion-pull-endpoint-tx-input"
							{...register("tx_id")}
						/>
					</Field>

					{errorMessage ? (
						<div
							className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
							role="alert"
							data-testid="conversion-pull-endpoint-form-error"
						>
							{errorMessage}
						</div>
					) : null}

					<div className="mt-auto flex items-center justify-end gap-2 pt-4">
						<button
							type="button"
							className={BTN_GHOST}
							onClick={onClose}
							disabled={submitting}
							data-testid="conversion-pull-endpoint-cancel"
						>
							Cancel
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={submitting}
							data-testid="conversion-pull-endpoint-submit"
						>
							{submitting ? "Saving" : "Create"}
						</button>
					</div>
				</form>
			</aside>
		</div>
	);
}

function Field({
	label,
	error,
	children,
}: {
	label: string;
	error?: string;
	children: ReactNode;
}) {
	return (
		<label className="flex flex-col gap-1">
			<span className={LABEL}>{label}</span>
			{children}
			{error ? (
				<span className="text-xs text-red-600" role="alert">
					{error}
				</span>
			) : null}
		</label>
	);
}

// BTN_DANGER kept for future row actions; silence unused-var linter noise.
void BTN_DANGER;
